"use client";

import { useEffect } from "react";

/**
 * A streamed page arrives as `<div hidden id="S:n">` and React's inline runtime queues it in
 * `window.$RB` as [boundary, segment] pairs, revealed on the next animation frame. A tab that
 * isn't painting (a background or automated tab) runs no frame, so when the client renders the
 * boundary itself (the router re-renders it while it waits), the server copy stays parked in
 * the DOM: every control twice, the first one dead. Removing a segment whose boundary is gone is
 * exactly what the reveal would do; one still waiting is left alone.
 */
function sweepStreamLeftovers(win: Window): number {
  const queue = (win as Window & { $RB?: unknown }).$RB;
  if (!Array.isArray(queue)) return 0;
  let removed = 0;
  // A nested boundary's placeholder sits inside its parent's copy and only goes with it, so
  // sweep until a pass removes nothing.
  for (let pass = -1; pass !== 0; ) {
    pass = 0;
    for (let at = 0; at + 1 < queue.length; at += 2) {
      const boundary = queue[at];
      const segment = queue[at + 1];
      if (isNode(boundary) && isNode(segment) && !boundary.isConnected && segment.isConnected) {
        segment.parentNode?.removeChild(segment);
        pass++;
      }
    }
    removed += pass;
  }
  return removed;
}

function isNode(value: unknown): value is Node {
  return typeof value === "object" && value !== null && "isConnected" in value;
}

function waiting(win: Window): boolean {
  const queue = (win as Window & { $RB?: unknown }).$RB;
  return (
    Array.isArray(queue) &&
    queue.some((entry, at) => at % 2 === 1 && isNode(entry) && entry.isConnected)
  );
}

/** Done once the document finished streaming and no segment is left waiting. */
function settled(win: Window): boolean {
  return win.document.readyState !== "loading" && !waiting(win);
}

/**
 * Watches the page until it has finished streaming and every segment is revealed or swept (the
 * shell usually hydrates before the page's segments arrive). Renders nothing.
 */
export function StreamLeftovers(): null {
  useEffect(() => {
    sweepStreamLeftovers(window);
    if (settled(window)) return;
    const check = () => {
      sweepStreamLeftovers(window);
      if (settled(window)) stop();
    };
    const observer = new MutationObserver(check);
    const stop = () => {
      observer.disconnect();
      document.removeEventListener("readystatechange", check);
    };
    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener("readystatechange", check);
    return stop;
  }, []);
  return null;
}
